const SITUATE_SPEAKER_PRESET_IDS = [
  "speakerphone-easy",
  "speakerphone-hard",
] as const

export type SituateSpeakerPresetId = (typeof SITUATE_SPEAKER_PRESET_IDS)[number]

const SITUATE_SPEAKER_PRESET_ID_SET: ReadonlySet<string> = new Set(
  SITUATE_SPEAKER_PRESET_IDS,
)

export const isSituateSpeakerPresetId = (
  value: unknown,
): value is SituateSpeakerPresetId =>
  typeof value === "string" && SITUATE_SPEAKER_PRESET_ID_SET.has(value)

const AUDIO_DEGRADATION_PRESET_IDS = [
  "landline",
  "cell-poor",
  "cell-handoff",
] as const

export type AudioDegradationPresetId =
  (typeof AUDIO_DEGRADATION_PRESET_IDS)[number]

const AUDIO_DEGRADATION_PRESET_ID_SET: ReadonlySet<string> = new Set(
  AUDIO_DEGRADATION_PRESET_IDS,
)

export const isAudioDegradationPresetId = (
  value: unknown,
): value is AudioDegradationPresetId =>
  typeof value === "string" && AUDIO_DEGRADATION_PRESET_ID_SET.has(value)

/**
 * Persisted shape of a persona's audio degradation setting. Unlike
 * situate_speaker, this is an object (not a bare preset id) so the backend
 * can evolve a preset's DSP parameters independently of the id it's keyed by.
 */
export interface AudioDegradationConfig {
  preset: AudioDegradationPresetId
  preset_version?: number
}

export interface VoicePersonaConfig {
  /**
   * The first message to send in the conversation
   */
  first_message?: string

  /**
   * The voice ID to use for speech synthesis
   * @default "5JEX0dllz9ikJpzCeiCC"
   */
  voice_id?: string

  /**
   * The language code for speech synthesis
   * @default "en"
   */
  language?: string

  /**
   * The background sound to use during the call
   * @default "off"
   */
  background_sound?: string

  /**
   * The volume level for background sound (minimum 0.1, no upper limit)
   * @default 0.3
   * @min 0.1
   */
  background_sound_volume?: number

  /**
   * Gain multiplier applied to the persona's voice output.
   * 1.0 leaves the voice unchanged; values < 1.0 attenuate, > 1.0 amplify.
   * @default 1.0
   * @min 0.0
   * @max 2.0
   */
  voice_volume?: number

  /**
   * Speech rate multiplier for the persona's voice. 1.0 = unchanged.
   * ElevenLabs voices are clamped server-side to [0.7, 1.2]; Google Chirp
   * voices accept the full slider range.
   * @default 1.0
   * @min 0.25
   * @max 2.0
   */
  voice_speed?: number

  /**
   * The temperature setting for the model
   * @default 0.2
   * @min 0
   * @max 2
   */
  temperature?: number

  /**
   * The maximum number of tokens to generate
   * @default 250
   * @min 50
   * @max 512
   */
  max_tokens?: number

  /**
   * Whether to detect emotion in the conversation
   */
  detect_emotion?: boolean

  /**
   * The voice model to use for VAPI
   * @default "eleven_multilingual_v2"
   */
  vapi_voice_model?: string

  /**
   * The model provider to use
   * @default "11labs"
   */
  model_provider?: string

  /**
   * The wait time in seconds before speaking
   * @default 0.4
   */
  wait_seconds?: number

  /**
   * The wait time in seconds on punctuation
   * @default 1.5
   */
  on_punctuation_seconds?: number

  /**
   * The wait time in seconds on no punctuation
   * @default 0.1
   */
  on_no_punctuation_seconds?: number

  /**
   * Multi-phone number configuration
   * Used for organizations with multi-phone support
   */
  multi_phone_config?: {
    /** The selected phone number index (1-16) */
    phone_number_index?: number
    /** Custom name for the selected phone number */
    phone_number_name?: string
  }

  /**
   * Conversation initiation mode
   * Controls whether the persona speaks first or waits for the user
   * @default "speak_first"
   */
  conversation_initiation?: "speak_first" | "wait_for_user"

  /**
   * Conversation initiator
   * Controls who speaks first in the conversation
   * @default "agent"
   */
  conversation_initiator?: "agent" | "persona"

  /**
   * The interruption rate for the persona
   * @default "NONE"
   */
  interruption_rate?: "NONE" | "LOW" | "MEDIUM" | "HIGH"

  /**
   * Preset describing where the speaker is situated acoustically.
   */
  situate_speaker?: SituateSpeakerPresetId

  /**
   * Preset simulating a degraded phone connection (e.g. a weak cell signal).
   * Mutually exclusive with situate_speaker.
   */
  audio_degradation?: AudioDegradationConfig
}
